import AspectRatioSelect, { videoClass } from "./AspectRatioSelect"
import { prepareMedia } from "./mediaContext"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Button, Select, Popconfirm } from "antd"
import { MdArrowBack, MdDownload, MdDelete, MdPlayCircle, MdChevronRight } from "react-icons/md"
import { loadScriptScenes } from "./loadScriptScenes"
import { premiumDurations } from "./aiTools"
import RenewableVideo from "./RenewableVideo"
import { downloadMedia } from "./mediaRenewal"
import { newRequestId } from "./videoApi"
import { getPremiumAccess, startPremiumVideo, listPremiumVideos, getPremiumVideo, deletePremiumVideo } from "./premiumApi"

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "")
const border = { borderColor: "var(--dashboard-border)" }
const deleteText = "The finished video and all of its working files are permanently deleted. This can't be undone."

const DeleteButton = ({ id, onDeleted, label = "Delete" }) => {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("")
  const remove = async () => { setBusy(true); setErr(""); try { await deletePremiumVideo(id); onDeleted() } catch (e) { setErr(e.message) } finally { setBusy(false) } }
  return (
    <span className="inline-flex flex-col gap-1">
      <Popconfirm title="Delete this Premium Video?" description={deleteText} okText="Delete permanently" okButtonProps={{ danger: true }} onConfirm={remove}>
        <Button danger size="small" className="rounded-full font-bold" icon={<MdDelete size={16} />} loading={busy} onClick={(e) => e.stopPropagation()}>{label}</Button>
      </Popconfirm>
      {err && <span role="alert" className="text-red-600 text-[13px]">{err}</span>}
    </span>
  )
}

// Saved Premium Videos: running, ready and failed ones. Polls while one is running.
const SavedPremium = ({ refreshKey, onOpen, onChanged, scriptId }) => {
  const [videos, setVideos] = useState(null)
  const load = useCallback(() => listPremiumVideos(scriptId).then(setVideos).catch(() => setVideos([])), [scriptId])
  useEffect(() => { load() }, [load, refreshKey])
  const running = (videos || []).some((v) => v.status === "processing")
  useEffect(() => { if (!running) return; const t = setInterval(() => { load(); onChanged() }, 10000); return () => clearInterval(t) }, [running, load, onChanged])
  if (!videos) return <p className="dashboard-muted text-[14px]">Loading saved Premium Videos…</p>
  return (
    <section aria-label="Saved Premium Videos" className="flex flex-col gap-2">
      <h4 className="font-bold text-[16px]">Saved Premium Videos</h4>
      {!videos.length && <p className="dashboard-muted text-[14px]">Your Premium Videos will appear here.</p>}
      {videos.map((v) => (
        <div key={v.id} className="flex items-center gap-3 rounded-xl border p-2 bg-white" style={border}>
          <button type="button" onClick={() => onOpen(v.id)} disabled={v.status !== "ready"} className="flex flex-1 min-w-0 items-center gap-3 text-left disabled:cursor-default">
            <div className="w-[96px] h-[54px] shrink-0 rounded-md flex items-center justify-center" style={{ background: "var(--dashboard-tint)" }}><MdPlayCircle size={24} color="var(--dashboard-primary)" /></div>
            <div className="min-w-0 flex-1 text-[13px]">
              <div className="font-bold text-[14px] truncate">{v.scriptTitle}{v.sceneNumber ? ` · Scene ${v.sceneNumber}` : ""}</div>
              <div className="dashboard-muted truncate">{v.sceneHeading}</div>
              <div className="dashboard-muted">{v.duration}s · {fmtDate(v.createdAt)}{v.status === "ready" && v.expiresAt ? ` · Available until ${fmtDate(v.expiresAt)}` : ""}</div>
              {v.status === "processing" && <div role="status" style={{ color: "var(--dashboard-primary)" }} className="font-bold">Generating… you can close the app and come back.</div>}
              {v.status === "failed" && <div className="text-red-600">{v.error}</div>}
              {v.deleteIncomplete && <div className="text-red-600">Some files could not be deleted yet. Please try Delete again.</div>}
            </div>
            {v.status === "ready" && <MdChevronRight size={22} className="dashboard-muted shrink-0" />}
          </button>
          {v.status !== "processing" && <DeleteButton id={v.id} onDeleted={load} />}
        </div>
      ))}
    </section>
  )
}

const PremiumPlayer = ({ video, onBack, onDeleted }) => {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState("")
  const download = async () => { setDownloading(true); setError(""); try { downloadMedia((await getPremiumVideo(video.id)).downloadUrl) } catch (e) { setError(e.message) } finally { setDownloading(false) } }
  return (
  <div className="flex flex-col gap-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="dashboard-muted text-[14px] min-w-0 truncate">Premium Video · {video.scriptTitle}{video.sceneNumber ? ` · Scene ${video.sceneNumber}` : ""} · {video.duration}s</p>
      <Button className="dashboard-tool rounded-full font-bold" icon={<MdArrowBack size={18} />} onClick={onBack}>Back</Button>
    </div>
    {video.playbackUrl ? <RenewableVideo resourceId={video.id} src={video.playbackUrl} renew={() => getPremiumVideo(video.id).then((v) => v.playbackUrl)} controls playsInline className={`w-full rounded-xl bg-black ${videoClass(video)}`} /> : <p className="dashboard-muted">This video is no longer available.</p>}
    <div className="text-[14px]">
      <div className="font-bold">{video.sceneHeading}</div>
      <div className="dashboard-muted text-[13px]">Generated {fmtDate(video.createdAt)}{video.expiresAt ? ` · Available until ${fmtDate(video.expiresAt)}` : ""}</div>
    </div>
    <div className="flex flex-wrap gap-3 items-start">
      {video.downloadUrl && <Button className="dashboard-primary rounded-full font-bold" icon={<MdDownload size={18} />} loading={downloading} onClick={download}>Download MP4</Button>}
      <DeleteButton id={video.id} onDeleted={onDeleted} label="Delete video" />
    </div>
    {error && <p role="alert" className="text-red-600 text-[14px]">{error}</p>}
  </div>
  )
}

// Setup: script → scene → 10 or 30 seconds → Generate. No storyboard needed.
const PremiumVideo = ({ scripts, scriptsLoading, fixedScript, beforeGenerate }) => {
  const [access, setAccess] = useState(null)
  const [scriptId, setScriptId] = useState(fixedScript?._id); const [scenes, setScenes] = useState([]); const [sceneId, setSceneId] = useState()
  const [aspectRatio, setAspectRatio] = useState("16:9")
  const [duration, setDuration] = useState(10)
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [notice, setNotice] = useState("")
  const [refreshKey, setRefreshKey] = useState(0); const [open, setOpen] = useState(null)
  const requestId = useRef(newRequestId())

  const loadAccess = useCallback(() => getPremiumAccess().then(setAccess).catch(() => setAccess({ balance: 0, adminTest: false })), [])
  useEffect(() => { loadAccess() }, [loadAccess, refreshKey])
  useEffect(() => {
    let cancelled = false
    setSceneId(undefined); setScenes([])
    if (scriptId) loadScriptScenes(scriptId).then(data => { if (!cancelled) setScenes(data) }).catch(() => { if (!cancelled) setScenes([]) })
    return () => { cancelled = true }
  }, [scriptId])
  useEffect(() => { requestId.current = newRequestId() }, [scriptId, sceneId, duration, aspectRatio])

  const cost = premiumDurations.find((d) => d.seconds === duration)?.credits
  const enough = access?.adminTest || (access?.balance || 0) >= cost
  const ready = access && enough && scriptId && sceneId && !busy

  const generate = async () => {
    if (!ready) return
    setBusy(true); setErr(""); setNotice("")
    try {
      await prepareMedia(beforeGenerate, scriptId, sceneId, scenes)
      await startPremiumVideo({ scriptId, sceneId, duration, aspectRatio, requestId: requestId.current })
      setNotice("Your Premium Video is generating in the background. It appears in Saved Premium Videos below — you can close the app and come back.")
      setRefreshKey((k) => k + 1)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const openVideo = async (id) => { setErr(""); try { setOpen(await getPremiumVideo(id)) } catch (e) { setErr(e.message) } }
  const back = () => { setOpen(null); setRefreshKey((k) => k + 1) }

  if (open) return <PremiumPlayer video={open} onBack={back} onDeleted={back} />

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="font-bold text-[18px]">Premium Video<span className="dashboard-muted font-normal text-[14px]"> · {premiumDurations.map((d) => `${d.seconds}s ${d.credits} credits`).join(" · ")}</span></h3>
        <p className="dashboard-muted text-[14px] mt-1">Made directly from your screenplay: the characters speak your scene's exact dialogue, with natural sound and movement. No storyboard needed.</p>
        <div className="text-[13px] mt-2 rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2" style={{ background: "var(--dashboard-tint)" }}>
          <span>AI credit balance: <b>{access?.balance ?? 0}</b></span>
        </div>
        {access?.adminTest && <p className="text-[13px] mt-2 font-bold">Admin testing — no AI credits are deducted.</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0">
          <label className="block font-bold text-[14px] mb-2">Choose script</label>
          <Select aria-label="Choose script" size="large" className="w-full" placeholder="Choose script" showSearch optionFilterProp="label" loading={scriptsLoading} disabled={!!fixedScript} value={scriptId} onChange={setScriptId}
            options={scripts.map((s) => ({ value: s._id, label: s.title || "Untitled Script" }))} />
        </div>
        <div className="min-w-0">
          <label className="block font-bold text-[14px] mb-2">Choose scene</label>
          <Select aria-label="Choose scene" size="large" className="w-full" placeholder="Choose scene" disabled={!scriptId} value={sceneId} onChange={setSceneId}
            options={scenes.map((s) => ({ value: s.id, label: `${s.index}. ${s.heading}` }))} />
        </div>
      </div>
      <AspectRatioSelect value={aspectRatio} onChange={setAspectRatio} />
      <div>
        <div className="font-bold text-[14px] mb-2">Length</div>
        <div className="flex flex-wrap gap-2">{premiumDurations.map((d) => (
          <button key={d.seconds} type="button" aria-pressed={duration === d.seconds} onClick={() => setDuration(d.seconds)} className="ai-chip rounded-full px-4 h-10 text-[14px]">{d.seconds} seconds · {d.credits} credits</button>))}</div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button className="dashboard-primary h-11 rounded-full px-5 font-bold" disabled={!ready} loading={busy} onClick={generate}>{`Generate Premium Video · ${cost} credits`}</Button>
      </div>
      {access && !enough && <p className="text-[13px] -mt-2">You need {cost} AI credits for this video.</p>}
      {notice && <p role="status" className="text-[14px] -mt-2" style={{ color: "var(--dashboard-primary)" }}>{notice}</p>}
      {err && <p role="alert" className="text-red-600 text-[14px] -mt-2">{err}</p>}
      <p className="dashboard-muted text-[12px] -mt-2">If a video can't be completed with your dialogue, your AI credits are returned automatically.</p>
      <SavedPremium key={scriptId || "all"} scriptId={scriptId} refreshKey={refreshKey} onOpen={openVideo} onChanged={loadAccess} />
    </div>
  )
}

export default PremiumVideo
